import { ProjectRenderer } from '../ProjectRenderer';
import { OrbitControls } from './OrbitControls';
import { CameraType } from '@/types/creator';
import { debounce } from 'lodash';
import { RendererMode, ThreeEvent } from '../types';
import { RoomWall } from '../objects/RoomWall';
import { RoomVertex } from '../objects/RoomVertex';
import { Measurement } from '../objects/Measurement';
import {
	PerspectiveCamera,
	OrthographicCamera,
	MathUtils,
	Box3,
	Vector3,
} from 'three';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import APP_CONFIG from '@/configs/appConfig';
import gsap from 'gsap';

export class CameraController {
	private _activeCamera: CameraType = APP_CONFIG.DEFAULT_CAMERA;

	perspectiveCamera!: PerspectiveCamera;
	orthographicCamera!: OrthographicCamera;
	perspectiveControls!: OrbitControls;
	orthographicControls!: OrbitControls;

	screenshotOrthographicCamera!: OrthographicCamera;
	screenshotPerspectiveCameras: PerspectiveCamera[] = [];

	constructor(private renderer: ProjectRenderer) {
		this.init();
	}

	get activeCamera() {
		return this._activeCamera;
	}

	set activeCamera(camera: CameraType) {
		this._activeCamera = camera;
		this.centerCamera();

		this.renderer.setAvailability();
	}

	private init() {
		this.createCameras();
		this.createControls();
	}

	private createCameras() {
		const aspect =
			this.renderer.canvas!.clientWidth / this.renderer.canvas!.clientHeight;

		this.orthographicCamera = new OrthographicCamera();
		this.screenshotOrthographicCamera = new OrthographicCamera();

		for (const orthographicCamera of [
			this.orthographicCamera,
			this.screenshotOrthographicCamera,
		]) {
			orthographicCamera.layers.enableAll();
			orthographicCamera.position.set(0, 15, 0);
			orthographicCamera.lookAt(0, 0, 0);
			orthographicCamera.zoom = RENDERER_CONFIG.CAMERA.ORTHO.INITIAL_ZOOM;
			orthographicCamera.updateProjectionMatrix();
		}

		this.screenshotOrthographicCamera.layers.disable(Measurement.LAYER);

		for (const cameraConfig of RENDERER_CONFIG.SCREENSHOT.PERSPECTIVE_CAMERAS) {
			const camera = new PerspectiveCamera();
			camera.layers.enableAll();
			camera.layers.disable(Measurement.LAYER);
			camera.aspect = aspect;
			camera.position.copy(cameraConfig.POSITION);
			camera.lookAt(cameraConfig.TARGET);
			camera.updateProjectionMatrix;
			this.screenshotPerspectiveCameras.push(camera);
		}

		this.perspectiveCamera = new PerspectiveCamera();
		for (const layer of RENDERER_CONFIG.CAMERA.PERSPECTIVE.ENABLED_LAYERS) {
			this.perspectiveCamera.layers.enable(layer);
		}
		this.perspectiveCamera.aspect = aspect;
		this.perspectiveCamera.position.set(5, 5, 5);

		this.perspectiveCamera.updateProjectionMatrix();
	}

	private createControls() {
		// Orthographic Controls

		this.orthographicControls = new OrbitControls(
			this.orthographicCamera,
			this.renderer.canvas
		);
		this.orthographicControls.target.set(0, 0, 0);
		this.orthographicControls.panSpeed = RENDERER_CONFIG.CONTROLS.PAN_SPEED;
		this.orthographicControls.enableDamping = true;
		this.orthographicControls.dampingFactor = 0.05;
		this.orthographicControls.enableRotate = false;

		this.orthographicControls.minZoom =
			RENDERER_CONFIG.CAMERA.ORTHO.MAX_ZOOM_OUT;
		this.orthographicControls.maxZoom =
			RENDERER_CONFIG.CAMERA.ORTHO.MAX_ZOOM_IN;

		this.orthographicControls.addEventListener(
			'change',
			debounce(() => {
				this.renderer.emitter.emit(
					ThreeEvent.ZOOM_LEVEL,
					this.orthographicCamera.zoom
				);
			}, 100)
		);

		this.orthographicControls.saveState();

		// Perspective controls

		this.perspectiveControls = new OrbitControls(
			this.perspectiveCamera,
			this.renderer.canvas
		);
		this.perspectiveControls.target.set(0, 0, 0);

		this.perspectiveControls.maxDistance =
			RENDERER_CONFIG.CAMERA.PERSPECTIVE.MAX_DISTANCE;
		this.perspectiveControls.minDistance =
			RENDERER_CONFIG.CAMERA.PERSPECTIVE.MIN_DISTANCE;

		this.perspectiveControls.panSpeed = RENDERER_CONFIG.CONTROLS.PAN_SPEED;
		this.perspectiveControls.enableDamping = true;
		this.perspectiveControls.dampingFactor = 0.05;

		if (import.meta.env.PROD)
			this.perspectiveControls.maxPolarAngle = MathUtils.degToRad(
				RENDERER_CONFIG.CONTROLS.MAX_POLAR_ANGLE
			);

		this.perspectiveControls.addEventListener(
			'change',
			debounce(() => {
				const distance = this.perspectiveCamera.position.distanceTo(
					this.perspectiveControls.target
				);

				this.renderer.emitter.emit(ThreeEvent.DISTANCE, distance);
			}, 150)
		);

		this.perspectiveControls.saveState();
	}

	prepareScreenshotCameras() {
		if (!this.renderer.roomManager.floor.mesh) return;

		const box3 = new Box3().setFromObject(this.renderer.roomManager.floor.mesh);

		const center = box3.getCenter(new Vector3());
		const size = box3.getSize(new Vector3());

		const aspect =
			RENDERER_CONFIG.SCREENSHOT.WIDTH / RENDERER_CONFIG.SCREENSHOT.HEIGHT;

		for (const perspectiveCamera of this.screenshotPerspectiveCameras) {
			perspectiveCamera.aspect = aspect;
			perspectiveCamera.updateProjectionMatrix();
		}

		let width = size.x + RENDERER_CONFIG.SCREENSHOT.FLOOR_SIZE_OFFSET;
		let height = size.z + RENDERER_CONFIG.SCREENSHOT.FLOOR_SIZE_OFFSET;

		if (width / height > aspect) height = width / aspect;
		else width = height * aspect;

		const halfW = width / 2;
		const halfH = height / 2;

		this.screenshotOrthographicCamera.left = -halfW;
		this.screenshotOrthographicCamera.right = halfW;
		this.screenshotOrthographicCamera.top = halfH;
		this.screenshotOrthographicCamera.bottom = -halfH;

		this.screenshotOrthographicCamera.position.set(center.x, 10, center.y);
		this.screenshotOrthographicCamera.lookAt(center.x, 0, center.y);

		this.screenshotOrthographicCamera.updateProjectionMatrix();
	}

	centerCamera() {
		if (!this.renderer.roomManager.floor.mesh) return;

		const box = new Box3().setFromObject(this.renderer.roomManager.floor.mesh);
		const size = new Vector3();
		box.getSize(size);

		const length = size.length();

		this.perspectiveControls.target.set(0, 0, 0);

		this.perspectiveCamera.position.set(0, length, length);
		this.perspectiveControls.update();
	}

	handleCameraZoom(zoom: number) {
		switch (this._activeCamera) {
			case CameraType.PERSPECTIVE: {
				const target = this.perspectiveControls.target.clone();
				const currentPosition = this.perspectiveCamera.position.clone();
				const direction = new Vector3()
					.subVectors(currentPosition, target)
					.normalize();

				const newPosition = target
					.clone()
					.add(direction.clone().multiplyScalar(zoom));

				gsap.to(this.perspectiveCamera.position, {
					x: newPosition.x,
					y: newPosition.y,
					z: newPosition.z,
					duration: 0.5,
					ease: 'power1.inOut',
					onUpdate: () => {
						this.perspectiveCamera.lookAt(target);
					},
					onComplete: () => {
						this.perspectiveControls.update();
					},
				});
				break;
			}
			case CameraType.ORTHOGRAPHIC: {
				gsap.to(this.orthographicCamera, {
					zoom: zoom,
					duration: 0.4,
					ease: 'power2.out',
					onUpdate: () => {
						this.orthographicCamera.updateProjectionMatrix();
					},
				});
				break;
			}
			default:
				break;
		}
	}

	handleResize(aspect: number) {
		const frustumSize = RENDERER_CONFIG.CAMERA.ORTHO.FRUSTRUM_SIZE;

		this.orthographicCamera.left = (-frustumSize * aspect) / 2;
		this.orthographicCamera.right = (frustumSize * aspect) / 2;
		this.orthographicCamera.top = frustumSize / 2;
		this.orthographicCamera.bottom = -frustumSize / 2;
		this.orthographicCamera.updateProjectionMatrix();

		this.perspectiveCamera.aspect = aspect;
		this.perspectiveCamera.updateProjectionMatrix();
	}

	setLayersVisibility() {
		if (this.renderer.roomManager.wallsVisible) {
			this.orthographicCamera.layers.enable(RoomWall.LAYER);
			this.perspectiveCamera.layers.enable(RoomWall.LAYER);
			this.orthographicCamera.layers.enable(RoomWall.LINES_CONGIF.LAYER);
			this.perspectiveCamera.layers.enable(RoomWall.LINES_CONGIF.LAYER);
			this.orthographicCamera.layers.enable(RoomVertex.LAYER);
			this.perspectiveCamera.layers.enable(RoomVertex.LAYER);
		} else {
			this.orthographicCamera.layers.disable(RoomWall.LAYER);
			this.perspectiveCamera.layers.disable(RoomWall.LAYER);
			this.orthographicCamera.layers.disable(RoomWall.LINES_CONGIF.LAYER);
			this.perspectiveCamera.layers.disable(RoomWall.LINES_CONGIF.LAYER);
			this.orthographicCamera.layers.disable(RoomVertex.LAYER);
			this.perspectiveCamera.layers.disable(RoomVertex.LAYER);
		}

		switch (this.renderer.mode) {
			case RendererMode.ROOM: {
			}
		}
	}

	dispose() {
		this.perspectiveControls.dispose();
	}
}
